import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import TranslateIcon from "@mui/icons-material/Translate";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { getDoctorProfile, updateDoctorAvailability } from "@/api/userApi";
import { addDays } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import {
  browserMatchesClinicZone,
  browserTimeZone,
  clinicTimeZoneOf,
  clinicZoneShortLabel,
  isoDowShortName,
} from "@/lib/clinicTime";
import {
  browserViewOfClinicRow,
  clinicRowsFromBrowserWindow,
} from "@/lib/availabilityFromBrowser";
import { PageHeader } from "@/components/common/PageHeader";
import { DoctorApprovalGate } from "@/components/doctor/DoctorApprovalGate";
import type { DoctorAvailability } from "@/types/domain";

const DAYS: { value: DoctorAvailability["dayOfWeek"]; label: string }[] = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 7, label: "Sunday" },
];

const HHMM_RE = /^\d{2}:\d{2}$/;

/** ISO day-of-week (1 = Mon … 7 = Sun) of "today" in the given IANA zone. */
function todayIsoDowInZone(zone: string): number {
  return Number(formatInTimeZone(new Date(), zone, "i"));
}

/**
 * DAYS sorted so that today (in the given zone) comes first, then tomorrow, …
 * giving the doctor an ascending-date dropdown for the next 7 days.
 */
function sortedDaysFrom(zone: string): typeof DAYS {
  const todayDow = todayIsoDowInZone(zone);
  const idx = DAYS.findIndex((d) => d.value === todayDow);
  return [...DAYS.slice(idx), ...DAYS.slice(0, idx)];
}

/**
 * Upcoming calendar date for an ISO DOW, relative to "today" **in the zone**
 * (not the browser's local date — those can differ across midnight).
 */
function getUpcomingDateForDow(isoDow: number, zone: string): Date {
  const todayStr = formatInTimeZone(new Date(), zone, "yyyy-MM-dd");
  const todayDow = todayIsoDowInZone(zone);
  const diff = (isoDow - todayDow + 7) % 7;
  const [y, m, d] = todayStr.split("-").map(Number);
  return new Date(y, m - 1, d + diff);
}

/** "Monday, 30 Apr" using the upcoming date in the given zone. */
function dowLabel(isoDow: number, dayName: string, zone: string): string {
  const date = getUpcomingDateForDow(isoDow, zone);
  const formatted = date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
  return `${dayName}, ${formatted}`;
}

/**
 * Next 15-minute boundary after "now" in the given IANA timezone.
 * E.g. 14:07 IST → "14:15", 14:45 IST → "15:00".
 */
function nextRounded15InZone(zone: string): string {
  const hhmm = formatInTimeZone(new Date(), zone, "HH:mm");
  const [h, m] = hhmm.split(":").map(Number);
  const totalMins = h * 60 + m + 1;
  const rounded = Math.ceil(totalMins / 15) * 15;
  const rh = Math.floor(rounded / 60);
  if (rh >= 24) return "23:45";
  const rm = rounded % 60;
  return `${String(rh).padStart(2, "0")}:${String(rm).padStart(2, "0")}`;
}

/** Clamps a HH:mm value so it's never before the given floor. */
function clampHhmm(value: string, floor: string): string {
  return value < floor ? floor : value;
}

function addMinutesToHhmm(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + minutes;
  if (total >= 24 * 60) return "23:45";
  const rh = Math.floor(total / 60);
  const rm = total % 60;
  return `${String(rh).padStart(2, "0")}:${String(rm).padStart(2, "0")}`;
}

/** Next calendar date (yyyy-MM-dd in zone) for an ISO weekday on or after today. */
function upcomingYmdForIsoDow(isoDow: number, zone: string): string {
  const todayStr = formatInTimeZone(new Date(), zone, "yyyy-MM-dd");
  const todayDow = todayIsoDowInZone(zone);
  const diff = (isoDow - todayDow + 7) % 7;
  const anchor = fromZonedTime(`${todayStr}T12:00:00`, zone);
  return formatInTimeZone(addDays(anchor, diff), zone, "yyyy-MM-dd");
}

function toHhMm(raw: string): string {
  if (!raw) return "";
  return raw.length >= 5 ? raw.slice(0, 5) : raw;
}

/**
 * A window is "past" when its next occurrence in the clinic zone has already
 * ended (compare calendar dates, not raw ISO DOW — otherwise Mon–Sat look
 * "past" on Sunday because 1–6 &lt; 7).
 */
function isWindowPastThisWeek(
  row: DoctorAvailability,
  clinicZone: string,
): boolean {
  const todayStr = formatInTimeZone(new Date(), clinicZone, "yyyy-MM-dd");
  const clinicNowHhmm = formatInTimeZone(new Date(), clinicZone, "HH:mm");
  const upcomingYmd = upcomingYmdForIsoDow(row.dayOfWeek, clinicZone);
  if (upcomingYmd > todayStr) return false;
  if (upcomingYmd < todayStr) return true;
  return row.endTime <= clinicNowHhmm;
}

/** Defaults for a new row that appears on the Upcoming tab (not yet elapsed). */
function buildDefaultNewAvailabilityRow(
  clinicZone: string,
): DoctorAvailability {
  const clinicTodayDow = todayIsoDowInZone(clinicZone);
  const clinicNowHhmm = formatInTimeZone(new Date(), clinicZone, "HH:mm");
  const clinicMinTime = nextRounded15InZone(clinicZone);

  for (let offset = 0; offset < 7; offset++) {
    const dow = (((clinicTodayDow - 1 + offset) % 7) +
      1) as DoctorAvailability["dayOfWeek"];
    const isToday = offset === 0;
    let start = isToday
      ? clinicMinTime > "09:00"
        ? clinicMinTime
        : "09:00"
      : "09:00";
    let end = "17:00";
    if (isToday && end <= clinicNowHhmm) {
      end = addMinutesToHhmm(start, 120);
      if (end <= clinicNowHhmm || end <= start) continue;
    }
    if (end <= start) end = addMinutesToHhmm(start, 60);
    const row: DoctorAvailability = {
      dayOfWeek: dow,
      startTime: start,
      endTime: end,
      isActive: true,
    };
    if (!isWindowPastThisWeek(row, clinicZone)) return row;
  }

  const start = clinicMinTime > "09:00" ? clinicMinTime : "09:00";
  return {
    dayOfWeek: clinicTodayDow as DoctorAvailability["dayOfWeek"],
    startTime: start,
    endTime: addMinutesToHhmm(start, 120),
    isActive: true,
  };
}

export default function DoctorSchedulePage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["doctor-profile"],
    queryFn: getDoctorProfile,
  });

  const [rows, setRows] = useState<DoctorAvailability[]>([]);
  const [editingRows, setEditingRows] = useState<Set<number>>(new Set());
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [helperOpen, setHelperOpen] = useState(false);

  const clinicZone = useMemo(
    () => clinicTimeZoneOf(data?.availabilityTimeZone),
    [data?.availabilityTimeZone],
  );
  const clinicLabel = useMemo(
    () => clinicZoneShortLabel(clinicZone),
    [clinicZone],
  );
  const browserZone = browserTimeZone();
  const browserDiffers = !browserMatchesClinicZone(clinicZone);

  const [activeTab, setActiveTab] = useState<0 | 1>(0);

  const clinicTodayDow = todayIsoDowInZone(clinicZone);
  const clinicSortedDays = sortedDaysFrom(clinicZone);
  const clinicMinTime = nextRounded15InZone(clinicZone);

  const isRowPastTime = (r: DoctorAvailability) =>
    r.dayOfWeek === clinicTodayDow && r.startTime < clinicMinTime;
  const hasPastTimeRows = rows.some(isRowPastTime);

  const indexedRows = rows.map((row, i) => ({ row, idx: i }));
  const upcomingRows = indexedRows.filter(
    ({ row }) => !isWindowPastThisWeek(row, clinicZone),
  );
  const pastRows = indexedRows.filter(({ row }) =>
    isWindowPastThisWeek(row, clinicZone),
  );

  useEffect(() => {
    if (data?.availability)
      setRows(
        data.availability.map((r) => ({
          ...r,
          startTime: toHhMm(r.startTime),
          endTime: toHhMm(r.endTime),
        })),
      );
  }, [data]);

  const save = useMutation({
    mutationFn: (body: DoctorAvailability[]) =>
      updateDoctorAvailability(
        body.map((r) => ({ ...r, isActive: r.isActive ?? true })),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doctor-profile"] });
      qc.invalidateQueries({ queryKey: ["slots"] });
      setEditingRows(new Set());
      setSaved(true);
      setSaveError(null);
    },
  });

  const handleSave = () => {
    const freshMin = nextRounded15InZone(clinicZone);
    const invalid = rows.some(
      (r) => r.dayOfWeek === clinicTodayDow && r.startTime < freshMin,
    );
    if (invalid) {
      setSaveError(
        `Some windows for today start before ${freshMin} ${clinicLabel}. Please update the start time or remove those windows.`,
      );
      return;
    }
    setSaveError(null);
    save.mutate(rows);
  };

  const addEmptyRow = () => {
    const newRow = buildDefaultNewAvailabilityRow(clinicZone);
    setActiveTab(0);
    setRows((prev) => {
      const idx = prev.length;
      setEditingRows((s) => new Set(s).add(idx));
      return [...prev, newRow];
    });
  };
  const appendRows = (toAppend: DoctorAvailability[]) =>
    setRows((prev) => {
      const startIdx = prev.length;
      setEditingRows((s) => {
        const next = new Set(s);
        toAppend.forEach((_, j) => next.add(startIdx + j));
        return next;
      });
      return [...prev, ...toAppend];
    });
  const removeRow = (i: number) => {
    setRows((r) => r.filter((_, idx) => idx !== i));
    setEditingRows((s) => {
      const next = new Set<number>();
      for (const idx of s) {
        if (idx < i) next.add(idx);
        else if (idx > i) next.add(idx - 1);
      }
      return next;
    });
  };
  const setRow = (i: number, patch: Partial<DoctorAvailability>) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const startEditing = (i: number) =>
    setEditingRows((s) => new Set(s).add(i));
  const stopEditing = (i: number) =>
    setEditingRows((s) => { const n = new Set(s); n.delete(i); return n; });

  const isApproved = data?.profileStatus === "APPROVED";
  if (data && !isApproved) {
    return (
      <Box>
        <PageHeader
          title="Availability"
          subtitle={`Consultation hours are saved in ${clinicZone} (${clinicLabel}). You can edit after approval.`}
        />
        <DoctorApprovalGate>{null}</DoctorApprovalGate>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Availability"
        subtitle={`Weekly windows are saved in ${clinicZone} (${clinicLabel}). Patients see them converted to their own zone when booking.`}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`Saving in ${clinicLabel} · ${clinicZone}`}
            />
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={save.isPending || hasPastTimeRows}
            >
              {save.isPending ? "Saving…" : "Save availability"}
            </Button>
          </Stack>
        }
      />

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      {browserDiffers && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography fontWeight={700} sx={{ mb: 0.5 }}>
            Heads up — the times you type below are in <u>{clinicLabel}</u>, not
            in your local clock.
          </Typography>
          <Typography variant="body2">
            Your browser is in <strong>{browserZone}</strong>, but ChikitsaLink
            stores every weekly window in <strong>{clinicLabel} ({clinicZone})</strong>{" "}
            — that&apos;s the clinic&apos;s business-hours clock. If you type{" "}
            <code>18:00</code>, that becomes <strong>6:00 PM {clinicLabel}</strong>{" "}
            for patients, which is a different instant from 6:00 PM in your
            zone. Use the <em>“Add window from my local time”</em> button below
            to type in your own zone and have us convert.
          </Typography>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v as 0 | 1)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          <Tab
            label={`Upcoming (${upcomingRows.length})`}
            icon={<AccessTimeIcon fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: "none", minHeight: 48 }}
          />
          <Tab
            label={`Past availability (${pastRows.length})`}
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: "none", minHeight: 48 }}
          />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* ── Upcoming tab ── */}
          {activeTab === 0 && (
            <>
              {upcomingRows.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {rows.length === 0
                    ? `You haven't added any availability yet. Click "Add window" to type a window directly in ${clinicLabel}, or "Add window from my local time" to type in ${browserZone} and have us convert.`
                    : "No upcoming availability windows. Add a new window below."}
                </Alert>
              )}

              <Stack spacing={1}>
                {upcomingRows.map(({ row, idx: i }) => {
                  const isEditing = editingRows.has(i);
                  const hasValidTimes =
                    HHMM_RE.test(row.startTime) && HHMM_RE.test(row.endTime);
                  const view = hasValidTimes
                    ? browserViewOfClinicRow(
                        row.dayOfWeek,
                        row.startTime,
                        row.endTime,
                        clinicZone,
                      )
                    : null;
                  const dayShifted =
                    browserDiffers &&
                    view != null &&
                    (view.browserStartDow !== row.dayOfWeek ||
                      view.browserEndDow !== row.dayOfWeek);
                  const dayLabel =
                    DAYS.find((d) => d.value === row.dayOfWeek)?.label ?? "";

                  if (!isEditing) {
                    return (
                      <Box key={i}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderRadius: 2,
                            bgcolor: "action.hover",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <AccessTimeIcon
                            fontSize="small"
                            sx={{ color: "primary.main" }}
                          />
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ minWidth: { sm: 130 } }}
                          >
                            {dayLabel}
                            {row.dayOfWeek === clinicTodayDow && (
                              <Chip
                                label="Today"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                              />
                            )}
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${row.startTime} – ${row.endTime}`}
                            sx={{ fontFamily: "monospace", fontWeight: 500 }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ flex: 1 }}
                          >
                            {clinicLabel}
                          </Typography>
                          <Tooltip title="Edit this window">
                            <IconButton
                              size="small"
                              onClick={() => startEditing(i)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove this window">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeRow(i)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        {browserDiffers && view && (
                          <Typography
                            variant="caption"
                            color={dayShifted ? "warning.main" : "text.secondary"}
                            sx={{ display: "block", mt: 0.5, ml: 5 }}
                          >
                            Your time: {isoDowShortName(view.browserStartDow)}{" "}
                            {view.browserStart} –{" "}
                            {isoDowShortName(view.browserEndDow)}{" "}
                            {view.browserEnd}
                            {dayShifted && " (day shift)"}
                          </Typography>
                        )}
                      </Box>
                    );
                  }

                  return (
                    <Box
                      key={i}
                      sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        border: "2px solid",
                        borderColor: "primary.main",
                        bgcolor: "background.paper",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ xs: "stretch", sm: "flex-start" }}
                      >
                        <TextField
                          select
                          label="Day"
                          value={row.dayOfWeek}
                          onChange={(e) =>
                            setRow(i, {
                              dayOfWeek: Number(
                                e.target.value,
                              ) as DoctorAvailability["dayOfWeek"],
                            })
                          }
                          size="small"
                          helperText=" "
                          sx={{ width: { sm: 240 }, flexShrink: 0 }}
                        >
                          {clinicSortedDays.map((d) => (
                            <MenuItem key={d.value} value={d.value}>
                              {dowLabel(d.value, d.label, clinicZone)}
                              {d.value === clinicTodayDow && (
                                <Chip
                                  label="Today"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          type="time"
                          label={`Start (${clinicLabel})`}
                          value={row.startTime}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (!v) return;
                            const isToday = row.dayOfWeek === clinicTodayDow;
                            const time = isToday
                              ? clampHhmm(toHhMm(v), clinicMinTime)
                              : toHhMm(v);
                            setRow(i, { startTime: time });
                          }}
                          inputProps={
                            row.dayOfWeek === clinicTodayDow
                              ? { min: clinicMinTime, step: 900 }
                              : { step: 900 }
                          }
                          size="small"
                          error={isRowPastTime(row)}
                          InputLabelProps={{ shrink: true }}
                          color="primary"
                          sx={{ width: { sm: 180 }, flexShrink: 0 }}
                          helperText={
                            isRowPastTime(row)
                              ? `past time — must be ${clinicMinTime} or later`
                              : row.dayOfWeek === clinicTodayDow
                                ? `today — from ${clinicMinTime} onwards`
                                : `saved as ${clinicLabel}`
                          }
                        />
                        <TextField
                          type="time"
                          label={`End (${clinicLabel})`}
                          value={row.endTime}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (!v) return;
                            setRow(i, { endTime: toHhMm(v) });
                          }}
                          inputProps={{ step: 900 }}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          color="primary"
                          sx={{ width: { sm: 180 }, flexShrink: 0 }}
                          helperText={`saved as ${clinicLabel}`}
                        />
                        <Tooltip title="Done editing">
                          <IconButton
                            color="primary"
                            onClick={() => stopEditing(i)}
                            sx={{ mt: { sm: "4px" } }}
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove this window">
                          <IconButton
                            color="error"
                            onClick={() => removeRow(i)}
                            sx={{ mt: { sm: "4px" } }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      {browserDiffers && view && (
                        <Typography
                          variant="caption"
                          color={dayShifted ? "warning.main" : "text.secondary"}
                          sx={{ display: "block", mt: 0.5, ml: 0.5 }}
                        >
                          In your browser ({browserZone}) this is{" "}
                          <strong>
                            {isoDowShortName(view.browserStartDow)}{" "}
                            {view.browserStart}
                            {" – "}
                            {isoDowShortName(view.browserEndDow)}{" "}
                            {view.browserEnd}
                          </strong>
                          {dayShifted &&
                            " — note the day shift across midnight"}
                          .
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 2 }}
                flexWrap="wrap"
                useFlexGap
              >
                <Button startIcon={<AddIcon />} onClick={addEmptyRow}>
                  Add window ({clinicLabel})
                </Button>
                {browserDiffers && (
                  <Button
                    startIcon={<TranslateIcon />}
                    variant="outlined"
                    onClick={() => setHelperOpen(true)}
                  >
                    Add window from my local time
                  </Button>
                )}
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 3 }}
              >
                Patients book in 15-minute slots within these windows.
                Overlapping windows on the same day are merged. On the calendar
                day that matches a window, only times from &ldquo;now&rdquo;
                onward ({clinicLabel}) become bookable slots for that day.
              </Typography>
            </>
          )}

          {/* ── Past availability tab (read-only) ── */}
          {activeTab === 1 && (
            <>
              {pastRows.length === 0 ? (
                <Alert severity="info">
                  No past availability windows for this week yet.
                </Alert>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    These windows have already elapsed this week. They cannot be
                    edited or removed. They will recur automatically next week.
                  </Alert>
                  <Stack spacing={1}>
                    {pastRows.map(({ row, idx: i }) => {
                      const hasValidTimes =
                        HHMM_RE.test(row.startTime) &&
                        HHMM_RE.test(row.endTime);
                      const view = hasValidTimes
                        ? browserViewOfClinicRow(
                            row.dayOfWeek,
                            row.startTime,
                            row.endTime,
                            clinicZone,
                          )
                        : null;
                      const dayShifted =
                        browserDiffers &&
                        view != null &&
                        (view.browserStartDow !== row.dayOfWeek ||
                          view.browserEndDow !== row.dayOfWeek);
                      const dayLabel =
                        DAYS.find((d) => d.value === row.dayOfWeek)?.label ??
                        "";

                      return (
                        <Box key={i}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                              px: 2,
                              py: 1.5,
                              borderRadius: 2,
                              bgcolor: "action.hover",
                              border: "1px solid",
                              borderColor: "divider",
                              opacity: 0.75,
                            }}
                          >
                            <HistoryIcon
                              fontSize="small"
                              sx={{ color: "text.disabled" }}
                            />
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ minWidth: { sm: 130 } }}
                            >
                              {dayLabel}
                              {row.dayOfWeek === clinicTodayDow && (
                                <Chip
                                  label="Today (elapsed)"
                                  size="small"
                                  color="default"
                                  variant="outlined"
                                  sx={{
                                    ml: 1,
                                    height: 18,
                                    fontSize: "0.65rem",
                                  }}
                                />
                              )}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${row.startTime} – ${row.endTime}`}
                              sx={{
                                fontFamily: "monospace",
                                fontWeight: 500,
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ flex: 1 }}
                            >
                              {clinicLabel}
                            </Typography>
                          </Stack>
                          {browserDiffers && view && (
                            <Typography
                              variant="caption"
                              color={
                                dayShifted ? "warning.main" : "text.secondary"
                              }
                              sx={{ display: "block", mt: 0.5, ml: 5 }}
                            >
                              Your time:{" "}
                              {isoDowShortName(view.browserStartDow)}{" "}
                              {view.browserStart} –{" "}
                              {isoDowShortName(view.browserEndDow)}{" "}
                              {view.browserEnd}
                              {dayShifted && " (day shift)"}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </>
              )}
            </>
          )}
        </Box>
      </Paper>

      {browserDiffers && activeTab === 0 && upcomingRows.length > 0 && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Preview — your saved windows side by side
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                <TableCell>Saved ({clinicLabel})</TableCell>
                <TableCell>Your view ({browserZone})</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => {
                const view =
                  HHMM_RE.test(r.startTime) && HHMM_RE.test(r.endTime)
                    ? browserViewOfClinicRow(
                        r.dayOfWeek,
                        r.startTime,
                        r.endTime,
                        clinicZone,
                      )
                    : null;
                return (
                  <TableRow key={i}>
                    <TableCell>{isoDowShortName(r.dayOfWeek)}</TableCell>
                    <TableCell>
                      {r.startTime} – {r.endTime}
                    </TableCell>
                    <TableCell>
                      {view
                        ? `${isoDowShortName(view.browserStartDow)} ${view.browserStart} – ${isoDowShortName(view.browserEndDow)} ${view.browserEnd}`
                        : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
      >
        <Alert severity="success" variant="filled">
          Availability saved in {clinicLabel}
        </Alert>
      </Snackbar>

      <FromBrowserWindowDialog
        open={helperOpen}
        onClose={() => setHelperOpen(false)}
        onAdd={(produced) => {
          appendRows(produced);
          setHelperOpen(false);
        }}
        clinicZone={clinicZone}
        clinicLabel={clinicLabel}
        browserZone={browserZone}
      />
    </Box>
  );
}

/**
 * Dialog where the doctor types a window in their **browser** zone.
 * On confirm, we compute the equivalent clinic-zone row(s) and append them
 * to the editable list — handling cross-midnight in the clinic zone by
 * splitting into two rows. The dialog never touches the persisted state
 * directly: the doctor still sees the result and can fine-tune before save.
 */
function FromBrowserWindowDialog({
  open,
  onClose,
  onAdd,
  clinicZone,
  clinicLabel,
  browserZone,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (rows: DoctorAvailability[]) => void;
  clinicZone: string;
  clinicLabel: string;
  browserZone: string;
}) {
  const browserTodayDow = todayIsoDowInZone(browserZone);
  const browserSortedDays = sortedDaysFrom(browserZone);
  const browserMinTime = nextRounded15InZone(browserZone);

  const [dow, setDow] = useState<DoctorAvailability["dayOfWeek"]>(
    browserTodayDow as DoctorAvailability["dayOfWeek"],
  );
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("13:00");

  const preview = useMemo(() => {
    if (!HHMM_RE.test(start) || !HHMM_RE.test(end)) return [];
    return clinicRowsFromBrowserWindow({
      browserDow: dow,
      browserStart: start,
      browserEnd: end,
      clinicZone,
    });
  }, [dow, start, end, clinicZone]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add window from your local time</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 2 }}>
          Type a single window in your browser zone ({browserZone}). We&apos;ll
          convert it to {clinicLabel} ({clinicZone}) before saving — and split
          into two rows automatically if it crosses midnight in {clinicLabel}.
        </DialogContentText>
        <Stack
          spacing={2}
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
        >
          <TextField
            select
            label={`Day in ${browserZone}`}
            value={dow}
            onChange={(e) =>
              setDow(Number(e.target.value) as DoctorAvailability["dayOfWeek"])
            }
            helperText=" "
            sx={{ width: { sm: 240 }, flexShrink: 0 }}
          >
            {browserSortedDays.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                {dowLabel(d.value, d.label, browserZone)}
                {d.value === browserTodayDow && (
                  <Chip
                    label="Today"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                  />
                )}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="time"
            label={`Start (${browserZone})`}
            value={start}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              const isToday = dow === browserTodayDow;
              const time = isToday
                ? clampHhmm(toHhMm(v), browserMinTime)
                : toHhMm(v);
              setStart(time);
            }}
            inputProps={
              dow === browserTodayDow
                ? { min: browserMinTime, step: 900 }
                : { step: 900 }
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: { sm: 180 }, flexShrink: 0 }}
            helperText={
              dow === browserTodayDow
                ? `from ${browserMinTime} onwards`
                : " "
            }
          />
          <TextField
            type="time"
            label={`End (${browserZone})`}
            value={end}
            onChange={(e) => setEnd(toHhMm(e.target.value))}
            inputProps={{ step: 900 }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { sm: 180 }, flexShrink: 0 }}
            helperText=" "
          />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Will be saved as
        </Typography>
        {preview.length === 0 ? (
          <Alert severity="warning">
            Enter a valid window where end is after start.
          </Alert>
        ) : (
          <Stack spacing={0.5}>
            {preview.map((p, i) => (
              <Typography key={i} variant="body2">
                <strong>{isoDowShortName(p.dayOfWeek)}</strong> {p.startTime} – {p.endTime}{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  ({clinicLabel})
                </Typography>
              </Typography>
            ))}
            {preview.length > 1 && (
              <Typography variant="caption" color="warning.main">
                Window crosses midnight in {clinicLabel} — saved as two rows
                with a 1-minute gap at midnight (slots are 15-minute aligned, so
                no bookable slot is lost).
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={preview.length === 0}
          onClick={() => onAdd(preview)}
        >
          Add to availability
        </Button>
      </DialogActions>
    </Dialog>
  );
}
